import { ReactNode } from 'react';

import { LuCircleAlert, LuClock, LuX } from 'react-icons/lu';

import { TrialBannerStage } from '../../utils/billing/getTrialBannerStage';

export interface TrialBannerProps {
  stage: TrialBannerStage;
  title: string;
  subtitle: ReactNode;
  ctaLabel: string;
  // Undefined for a plain member: `ctaLabel` then renders as plain text (e.g.
  // "Ask your team owner to choose a plan") instead of a button, since a
  // member can't act on it.
  onCtaClick?: () => void;
  isDismissible: boolean;
  onDismiss: () => void;
  dismissAriaLabel: string;
}

const stylesheet = {
  base: 'hidden md:flex items-center gap-3 py-[11px] px-6 text-[13px] flex-none',
  calm: 'bg-semantic-surface-2 border-b border-semantic-border',
  warning:
    'bg-semantic-bg-warning-subtle border-b border-semantic-border-warning-subtle',
  danger:
    'bg-semantic-bg-danger-subtle border-b border-semantic-border-danger-subtle',
  dot: 'w-[7px] h-[7px] flex-none rounded-full bg-semantic-bg-accent',
  iconWarning: 'flex-none text-semantic-status-warning',
  iconDanger: 'flex-none text-semantic-text-danger',
  title: 'font-bold whitespace-nowrap text-semantic-text-primary',
  titleDanger: 'font-bold whitespace-nowrap text-semantic-text-danger',
  subtitle: 'inline text-semantic-text-secondary whitespace-nowrap',
  spacer: 'flex-1',
  ctaLink:
    'font-bold whitespace-nowrap text-semantic-text-accent hover:text-semantic-bg-accent-hover bg-transparent border-none p-0 cursor-pointer',
  ctaSolid:
    'bg-semantic-bg-accent hover:bg-semantic-bg-accent-hover text-white font-bold whitespace-nowrap px-3.5 py-1.5 rounded-semantic border-none cursor-pointer',
  dismiss:
    'flex-none flex items-center justify-center text-semantic-text-tertiary hover:text-semantic-text-primary bg-transparent border-none p-0.5 cursor-pointer',
};

function resolveStageClassName(stage: TrialBannerStage): string {
  if (stage === TrialBannerStage.URGENT || stage === TrialBannerStage.EXPIRED) {
    return stylesheet.danger;
  }
  if (stage === TrialBannerStage.WARNING) {
    return stylesheet.warning;
  }
  return stylesheet.calm;
}

function buildContainerClassName(stage: TrialBannerStage): string {
  return `${stylesheet.base} ${resolveStageClassName(stage)}`;
}

export function TrialBanner({
  stage,
  title,
  subtitle,
  ctaLabel,
  onCtaClick,
  isDismissible,
  onDismiss,
  dismissAriaLabel,
}: Readonly<TrialBannerProps>) {
  const isDanger =
    stage === TrialBannerStage.URGENT || stage === TrialBannerStage.EXPIRED;
  const isWarning = stage === TrialBannerStage.WARNING;
  const isCalm = stage === TrialBannerStage.CALM;

  return (
    <div
      className={buildContainerClassName(stage)}
      role={isDanger ? 'alert' : 'status'}
    >
      {isCalm && <span className={stylesheet.dot} aria-hidden="true" />}
      {isWarning && (
        <LuClock className={stylesheet.iconWarning} aria-hidden="true" />
      )}
      {isDanger && (
        <LuCircleAlert className={stylesheet.iconDanger} aria-hidden="true" />
      )}

      <span className={isDanger ? stylesheet.titleDanger : stylesheet.title}>
        {title}
      </span>
      <span className={stylesheet.subtitle}>{subtitle}</span>

      <span className={stylesheet.spacer} />

      {onCtaClick ? (
        <button
          type="button"
          className={isCalm ? stylesheet.ctaLink : stylesheet.ctaSolid}
          onClick={onCtaClick}
        >
          {ctaLabel}
        </button>
      ) : (
        <span className={stylesheet.subtitle}>{ctaLabel}</span>
      )}

      {isDismissible && (
        <button
          type="button"
          className={stylesheet.dismiss}
          onClick={onDismiss}
          aria-label={dismissAriaLabel}
        >
          <LuX aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
